import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  XMarkIcon,
  KeyIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  TrashIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { API_BASE_URL } from "@time-theft-analyzer/shared";
import {
  mcpKeyApi,
  McpKeyStatus,
  McpKeyCreatePayload,
} from "../services/mcpKeyApi";
import { formatDate } from "../i18n/format";
import { buildMcpSnippets } from "./mcpSnippets";

interface McpKeyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Derive the MCP server URL (`<host>/mcp`) from the same backend base URL the app
 * already uses for API calls — never hardcoded. `API_BASE_URL` is the configured
 * backend origin (`REACT_APP_API_URL`, falling back to localhost for local dev).
 */
const getMcpServerUrl = (): string => {
  const base = (API_BASE_URL || "").replace(/\/+$/, "");
  return `${base}/mcp`;
};

/** Placeholder shown in snippets before a key is revealed this session. */
const KEY_PLACEHOLDER = "<your-key>";

// Expiry presets (in days). `null` means "no expiry".
const EXPIRY_PRESETS: { days: number | null; labelKey: string }[] = [
  { days: 30, labelKey: "mcpKey.expiry30" },
  { days: 90, labelKey: "mcpKey.expiry90" },
  { days: 365, labelKey: "mcpKey.expiry365" },
  { days: null, labelKey: "mcpKey.expiryNever" },
];

const McpKeyModal: React.FC<McpKeyModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation("common");

  const [status, setStatus] = useState<McpKeyStatus | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [expiryDays, setExpiryDays] = useState<number | null>(90);
  const [generating, setGenerating] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(false);

  // The raw key is held ONLY in local component state for the single reveal, and is wiped
  // whenever the modal closes — it is never persisted anywhere.
  const [rawKey, setRawKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      setLoadingStatus(true);
      const res = await mcpKeyApi.getStatus();
      setStatus(res.data);
    } catch (error) {
      console.error("Failed to load MCP key status:", error);
      toast.error(t("mcpKey.statusError"));
    } finally {
      setLoadingStatus(false);
    }
  }, [t]);

  useEffect(() => {
    if (isOpen) {
      fetchStatus();
    } else {
      // Wipe any revealed key + transient state on close.
      setRawKey(null);
      setCopied(false);
      setConfirmRevoke(false);
    }
  }, [isOpen, fetchStatus]);

  if (!isOpen) return null;

  const handleGenerate = async () => {
    try {
      setGenerating(true);
      const payload: McpKeyCreatePayload =
        expiryDays === null
          ? { noExpiry: true }
          : {
              expiresAt: new Date(
                Date.now() + expiryDays * 24 * 60 * 60 * 1000
              ).toISOString(),
            };
      const res = await mcpKeyApi.create(payload);
      setRawKey(res.data.accessKey);
      setCopied(false);
      toast.success(t("mcpKey.generateSuccess"));
      // Refresh non-secret status (prefix / dates) after generation.
      fetchStatus();
    } catch (error) {
      console.error("Failed to generate MCP key:", error);
      toast.error(t("mcpKey.generateError"));
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = async () => {
    if (!rawKey) return;
    try {
      await navigator.clipboard.writeText(rawKey);
      setCopied(true);
      toast.success(t("mcpKey.copied"));
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t("mcpKey.copyError"));
    }
  };

  const handleRevoke = async () => {
    try {
      setRevoking(true);
      await mcpKeyApi.revoke();
      setRawKey(null);
      setConfirmRevoke(false);
      toast.success(t("mcpKey.revokeSuccess"));
      fetchStatus();
    } catch (error) {
      console.error("Failed to revoke MCP key:", error);
      toast.error(t("mcpKey.revokeError"));
    } finally {
      setRevoking(false);
    }
  };

  const exists = Boolean(status?.exists);

  // Connection config. The server URL is derived from the app's backend origin (not
  // hardcoded). Snippets interpolate the raw key when one was just revealed, otherwise a
  // placeholder so the config is always shown.
  const serverUrl = getMcpServerUrl();
  const keyForSnippet = rawKey ?? KEY_PLACEHOLDER;
  const snippets = buildMcpSnippets(serverUrl, keyForSnippet);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-tk-text/30 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-tk-card-hover border border-tk-border animate-scale-in max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 p-6 border-b border-tk-border/60">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-2xl bg-gradient-to-br from-tk-accent to-tk-violet flex items-center justify-center shadow-lg shadow-tk-accent/25 flex-shrink-0">
              <KeyIcon className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-tk-text tracking-tight">
                {t("mcpKey.title")}
              </h2>
              <p className="text-sm text-tk-text-secondary mt-0.5">
                {t("mcpKey.subtitle")}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-xl text-tk-text-muted hover:bg-tk-base hover:text-tk-text transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-tk-accent/30"
            aria-label={t("actions.close")}
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Helper text */}
          <p className="text-sm text-tk-text-secondary leading-relaxed">
            {t("mcpKey.helper")}{" "}
            <code className="px-1.5 py-0.5 rounded-md bg-tk-base border border-tk-border text-xs font-mono-tk text-tk-text">
              X-API-Key
            </code>{" "}
            {t("mcpKey.helperEndpoint")}{" "}
            <code className="px-1.5 py-0.5 rounded-md bg-tk-base border border-tk-border text-xs font-mono-tk text-tk-text">
              /mcp
            </code>
            .
          </p>

          {/* One-time raw key reveal */}
          {rawKey && (
            <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4">
              <div className="flex items-start gap-2.5">
                <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-amber-800">
                    {t("mcpKey.warningTitle")}
                  </p>
                  <p className="text-xs text-amber-700 mt-0.5">
                    {t("mcpKey.warningBody")}
                  </p>
                  <div className="mt-3 flex items-stretch gap-2">
                    <code className="flex-1 min-w-0 px-3 py-2 rounded-xl bg-white border border-amber-300 text-xs font-mono-tk text-tk-text break-all">
                      {rawKey}
                    </code>
                    <button
                      type="button"
                      onClick={handleCopy}
                      className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-amber-600 text-white text-xs font-semibold hover:bg-amber-700 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-amber-500/40"
                    >
                      {copied ? (
                        <CheckIcon className="h-4 w-4" />
                      ) : (
                        <ClipboardDocumentIcon className="h-4 w-4" />
                      )}
                      {copied ? t("mcpKey.copiedShort") : t("mcpKey.copy")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Connection config */}
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-tk-text-muted mb-2">
              {t("mcpKey.connectHeading")}
            </h3>
            <p className="text-sm text-tk-text-secondary leading-relaxed mb-3">
              {t("mcpKey.connectIntro")}
              {!rawKey && (
                <>
                  {" "}
                  <span className="text-tk-text-muted">
                    {t("mcpKey.connectKeyPlaceholderNote")}
                  </span>
                </>
              )}
            </p>

            <div className="rounded-2xl border border-tk-border bg-tk-base/60 p-4 space-y-2.5">
              <StatusRow
                label={t("mcpKey.fieldServerUrl")}
                value={serverUrl}
                mono
                wrap
              />
              <StatusRow
                label={t("mcpKey.fieldTransport")}
                value={t("mcpKey.valueTransport")}
              />
              <StatusRow
                label={t("mcpKey.fieldAuth")}
                value={`X-API-Key: ${keyForSnippet}`}
                mono
                wrap
              />
              <p className="text-xs text-tk-text-muted pt-0.5">
                {t("mcpKey.authNote")}
              </p>
            </div>

            <h4 className="text-xs font-semibold uppercase tracking-wider text-tk-text-muted mt-4 mb-2">
              {t("mcpKey.snippetsHeading")}
            </h4>
            <div className="space-y-3">
              {snippets.map((snippet) => (
                <SnippetBlock
                  key={snippet.labelKey}
                  label={t(snippet.labelKey)}
                  code={snippet.code}
                  copyLabel={t("mcpKey.copySnippet")}
                  copiedLabel={t("mcpKey.copiedShort")}
                  copiedToast={t("mcpKey.snippetCopied")}
                  copyErrorToast={t("mcpKey.copyError")}
                />
              ))}

              {/* Web connectors — OAuth pending, no fabricated endpoints. */}
              <div className="rounded-xl border border-dashed border-tk-border bg-tk-base/40 p-3">
                <p className="text-xs font-semibold text-tk-text-secondary">
                  {t("mcpKey.snippetWebConnectors")}
                </p>
                <p className="text-xs text-tk-text-muted mt-0.5">
                  {t("mcpKey.webConnectorsNote")}
                </p>
              </div>
            </div>
          </div>

          {/* Current status */}
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-tk-text-muted mb-2">
              {t("mcpKey.statusHeading")}
            </h3>
            {loadingStatus ? (
              <div className="flex items-center gap-2 text-sm text-tk-text-muted py-3">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-tk-border border-t-tk-accent" />
                {t("mcpKey.statusLoading")}
              </div>
            ) : exists ? (
              <div className="rounded-2xl border border-tk-border bg-tk-base/60 p-4 space-y-2.5">
                <StatusRow
                  label={t("mcpKey.fieldPrefix")}
                  value={
                    status?.prefix ? `${status.prefix}…` : t("mcpKey.valueUnknown")
                  }
                  mono
                />
                <StatusRow
                  label={t("mcpKey.fieldCreated")}
                  value={
                    status?.createdAt
                      ? formatDate(status.createdAt)
                      : t("mcpKey.valueUnknown")
                  }
                />
                <StatusRow
                  label={t("mcpKey.fieldExpires")}
                  value={
                    status?.expiresAt
                      ? formatDate(status.expiresAt)
                      : t("mcpKey.valueNever")
                  }
                />
                <StatusRow
                  label={t("mcpKey.fieldLastUsed")}
                  value={
                    status?.lastUsedAt
                      ? formatDate(status.lastUsedAt)
                      : t("mcpKey.valueNeverUsed")
                  }
                />
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-tk-border bg-tk-base/40 p-4 text-sm text-tk-text-secondary">
                {t("mcpKey.statusNone")}
              </div>
            )}
          </div>

          {/* Generate / regenerate */}
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-tk-text-muted mb-2">
              {exists ? t("mcpKey.regenerateHeading") : t("mcpKey.generateHeading")}
            </h3>
            {exists && (
              <p className="text-xs text-tk-text-muted mb-3">
                {t("mcpKey.regenerateHint")}
              </p>
            )}
            <label className="block text-sm font-medium text-tk-text-secondary mb-1.5">
              {t("mcpKey.expiryLabel")}
            </label>
            <select
              value={expiryDays === null ? "never" : String(expiryDays)}
              onChange={(e) =>
                setExpiryDays(
                  e.target.value === "never" ? null : Number(e.target.value)
                )
              }
              className="w-full px-3.5 py-2.5 bg-white border border-tk-border rounded-xl text-sm text-tk-text focus:outline-none focus:ring-4 focus:ring-tk-accent/10 focus:border-tk-accent/40 transition-all duration-200"
            >
              {EXPIRY_PRESETS.map((preset) => (
                <option
                  key={preset.labelKey}
                  value={preset.days === null ? "never" : String(preset.days)}
                >
                  {t(preset.labelKey)}
                </option>
              ))}
            </select>

            <button
              type="button"
              onClick={handleGenerate}
              disabled={generating}
              className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-tk-accent to-tk-violet text-white text-sm font-semibold shadow-lg shadow-tk-accent/25 hover:opacity-95 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-tk-accent/20"
            >
              <KeyIcon className="h-4 w-4" />
              {generating
                ? t("mcpKey.generating")
                : exists
                  ? t("mcpKey.regenerateButton")
                  : t("mcpKey.generateButton")}
            </button>
          </div>

          {/* Revoke */}
          {exists && (
            <div className="pt-2 border-t border-tk-border/60">
              {confirmRevoke ? (
                <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4">
                  <p className="text-sm font-medium text-rose-800">
                    {t("mcpKey.revokeConfirmTitle")}
                  </p>
                  <p className="text-xs text-rose-600 mt-0.5">
                    {t("mcpKey.revokeConfirmBody")}
                  </p>
                  <div className="mt-3 flex gap-2">
                    <button
                      type="button"
                      onClick={handleRevoke}
                      disabled={revoking}
                      className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-rose-600 text-white text-sm font-semibold hover:bg-rose-700 disabled:opacity-60 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-rose-500/40"
                    >
                      <TrashIcon className="h-4 w-4" />
                      {revoking ? t("mcpKey.revoking") : t("mcpKey.revokeConfirmButton")}
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmRevoke(false)}
                      disabled={revoking}
                      className="px-3.5 py-2 rounded-xl text-sm font-medium text-tk-text-secondary hover:bg-tk-base transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-tk-accent/30"
                    >
                      {t("actions.cancel")}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmRevoke(true)}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-medium text-rose-600 hover:bg-rose-50 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-rose-500/30"
                >
                  <TrashIcon className="h-4 w-4" />
                  {t("mcpKey.revokeButton")}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const SnippetBlock: React.FC<{
  label: string;
  code: string;
  copyLabel: string;
  copiedLabel: string;
  copiedToast: string;
  copyErrorToast: string;
}> = ({ label, code, copyLabel, copiedLabel, copiedToast, copyErrorToast }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success(copiedToast);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(copyErrorToast);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="text-xs font-medium text-tk-text-secondary">{label}</span>
        <button
          type="button"
          onClick={handleCopy}
          className="flex-shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-tk-base border border-tk-border text-xs font-semibold text-tk-text-secondary hover:bg-white hover:text-tk-text transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-tk-accent/30"
        >
          {copied ? (
            <CheckIcon className="h-3.5 w-3.5" />
          ) : (
            <ClipboardDocumentIcon className="h-3.5 w-3.5" />
          )}
          {copied ? copiedLabel : copyLabel}
        </button>
      </div>
      <pre className="px-3 py-2 rounded-xl bg-tk-text/5 border border-tk-border text-xs font-mono-tk text-tk-text whitespace-pre-wrap break-all">
        {code}
      </pre>
    </div>
  );
};

const StatusRow: React.FC<{
  label: string;
  value: string;
  mono?: boolean;
  wrap?: boolean;
}> = ({ label, value, mono, wrap }) => (
  <div className="flex items-start justify-between gap-3 text-sm">
    <span className="text-tk-text-muted flex-shrink-0">{label}</span>
    <span
      className={`text-tk-text font-medium ${wrap ? "text-right break-all" : "truncate"} ${
        mono ? "font-mono-tk" : ""
      }`}
    >
      {value}
    </span>
  </div>
);

export default McpKeyModal;
